import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CalendarClock, Loader2, MapPin, Clock } from "lucide-react";
import { useSeo } from "../hooks/useSeo";
import { PageHeader } from "../components/PageHeader";
import { Reveal } from "../lib/motionx";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { site, tourTimeSlots } from "../data/site";
import { useLang } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const today = () => new Date().toISOString().split("T")[0];

const empty = {
  name: "",
  phone: "",
  email: "",
  clinic: site.clinics[0].address,
  preferred_date: "",
  preferred_time: tourTimeSlots[0],
  party: "",
  message: "",
};

export default function ScheduleTour() {
  const { t } = useLang();
  useSeo({
    title: "Schedule a Tour | Amenity Health Services",
    description:
      "Book a tour of an Amenity Health Services location in Houston, TX. Tours available Monday–Friday, 8 AM–6 PM.",
  });
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error(t("Please add your name."));
    if (!form.email.trim() && !form.phone.trim())
      return toast.error(t("Please provide an email or a phone number."));
    if (!form.preferred_date) return toast.error(t("Please choose a preferred date."));
    const day = new Date(form.preferred_date + "T00:00:00").getDay();
    if (day === 0 || day === 6)
      return toast.error(t("Tours are available Monday–Friday. Please pick a weekday."));
    setLoading(true);
    try {
      const payload = { ...form };
      if (!payload.email) delete payload.email;
      await axios.post(`${API}/tours`, payload);
      toast.success(t("Tour request received! We'll confirm your visit soon."));
      setForm(empty);
    } catch {
      toast.error(t("Something went wrong. Please try again or call us."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageHeader
        label="Visit us"
        titleLines={["Schedule", "a tour"]}
        intro="Come see our space and meet our team. Choose a location and a time that works for your family — we'll take care of the rest."
      />

      <section className="py-8 sm:py-14">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-5 gap-10">
          <Reveal className="lg:col-span-2 space-y-4">
            <div className="rounded-3xl border border-black/5 bg-white p-7">
              <h2 className="font-heading font-semibold text-lg text-ink flex items-center gap-2 mb-4">
                <Clock size={18} className="text-teal" /> {t("Tour hours")}
              </h2>
              <ul className="space-y-3">
                {site.hours.map((h) => (
                  <li key={h.day} className="flex items-center justify-between text-sm text-ink/70 border-b border-black/5 pb-2.5 last:border-0">
                    <span>{t(h.day)}</span>
                    <span className="font-medium text-ink">{t(h.time)}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-3xl border border-black/5 bg-white p-7">
              <h2 className="font-heading font-semibold text-lg text-ink flex items-center gap-2 mb-4">
                <MapPin size={18} className="text-teal" /> {t("Our locations")}
              </h2>
              <ul className="space-y-3">
                {site.clinics.map((c) => (
                  <li key={c.address} className="text-sm">
                    <span className="block font-medium text-ink">{c.name}</span>
                    <span className="block text-ink/60">{c.address}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>

          <Reveal className="lg:col-span-3" delay={0.1}>
            <form onSubmit={submit} data-testid="tour-form" className="rounded-[2rem] border border-black/5 bg-cream p-7 sm:p-10 space-y-6">
              <h2 className="font-heading font-semibold text-2xl text-ink flex items-center gap-2">
                <CalendarClock size={22} className="text-teal" /> {t("Request your tour")}
              </h2>

              <div className="grid gap-6 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="t-name">{t("Full name")} <span className="text-destructive">*</span></Label>
                  <Input id="t-name" data-testid="tour-name-input" value={form.name} onChange={set("name")} placeholder={t("Your name")} className="h-12 rounded-xl bg-white" required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="t-phone">{t("Phone")}</Label>
                  <Input id="t-phone" data-testid="tour-phone-input" value={form.phone} onChange={set("phone")} placeholder="(346) 333-2794" inputMode="tel" className="h-12 rounded-xl bg-white" />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="t-email">{t("Email")}</Label>
                <Input id="t-email" data-testid="tour-email-input" type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" className="h-12 rounded-xl bg-white" />
              </div>

              <div className="space-y-2">
                <Label htmlFor="t-clinic">{t("Which location?")}</Label>
                <select id="t-clinic" data-testid="tour-clinic-select" value={form.clinic} onChange={set("clinic")} className="h-12 w-full rounded-xl border border-input bg-white px-3 text-[0.95rem] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                  {site.clinics.map((c) => (
                    <option key={c.address} value={c.address}>{c.name} — {c.address}</option>
                  ))}
                </select>
              </div>

              <div className="grid gap-6 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="t-date">{t("Preferred date")} <span className="text-destructive">*</span></Label>
                  <Input id="t-date" data-testid="tour-date-input" type="date" min={today()} value={form.preferred_date} onChange={set("preferred_date")} className="h-12 rounded-xl bg-white" required />
                  <p className="text-xs text-ink/50">{t("Weekdays only (Mon–Fri).")}</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="t-time">{t("Preferred time")} <span className="text-destructive">*</span></Label>
                  <select id="t-time" data-testid="tour-time-select" value={form.preferred_time} onChange={set("preferred_time")} className="h-12 w-full rounded-xl border border-input bg-white px-3 text-[0.95rem] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                    {tourTimeSlots.map((ts) => <option key={ts} value={ts}>{ts}</option>)}
                  </select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="t-party">{t("Who's visiting?")} <span className="text-ink/40 font-normal">{t("(optional)")}</span></Label>
                <Input id="t-party" data-testid="tour-party-input" value={form.party} onChange={set("party")} placeholder={t("e.g., myself and my child")} className="h-12 rounded-xl bg-white" />
              </div>

              <div className="space-y-2">
                <Label htmlFor="t-message">{t("Anything we should know?")} <span className="text-ink/40 font-normal">{t("(optional)")}</span></Label>
                <Textarea id="t-message" data-testid="tour-message-input" value={form.message} onChange={set("message")} placeholder={t("Share any questions. Please don't include private medical information.")} rows={4} className="rounded-xl bg-white resize-none" />
              </div>

              <button type="submit" data-testid="tour-submit-btn" disabled={loading} className="inline-flex items-center justify-center gap-2 rounded-full bg-teal px-7 py-3.5 font-medium text-white hover:bg-[#2E6088] transition-colors disabled:opacity-60 w-full sm:w-auto">
                {loading ? <Loader2 size={18} className="animate-spin" /> : <CalendarClock size={18} />}
                {loading ? t("Sending…") : t("Request Tour")}
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </>
  );
}
